import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ArrowRight, Briefcase, Building2, CheckCircle2, Clock, Factory,
  GraduationCap, MapPin, Network, Upload, Sparkles, AlertCircle, Loader2, FileText,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  nome: "",
  email: "",
  telefone: "",
  cidade: "",
  escolaridade: "",
  experiencia_anos: "",
  pretensao_salarial: "",
  linkedin: "",
  carta_apresentacao: "",
  consentimento_lgpd: false,
  curriculo_base64: "",
  curriculo_nome: "",
};

const MAX_FILE_BYTES = 4 * 1024 * 1024; // 4 MB

function maskPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{0,4})(\d{0,4}).*/, (_, a, b, c) => [a && `(${a}`, a && ") ", b, c && `-${c}`].filter(Boolean).join(""));
  return d.replace(/(\d{2})(\d{5})(\d{0,4}).*/, "($1) $2-$3");
}

export default function VacancyPublicPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [vac, setVac] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [fileInfo, setFileInfo] = useState(null);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await axios.get(`${API_BASE}/public/vacancies/${id}`);
        setVac(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    setFileError("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(pdf|doc|docx)$/i.test(f.name)) {
      setFileError("Aceitamos apenas arquivos PDF, DOC ou DOCX.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError(`Arquivo muito grande. Limite: ${(MAX_FILE_BYTES / 1024 / 1024).toFixed(0)} MB.`);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, curriculo_base64: reader.result, curriculo_nome: f.name }));
      setFileInfo({ name: f.name, size: f.size });
    };
    reader.readAsDataURL(f);
  };

  const removeFile = () => {
    setForm((f) => ({ ...f, curriculo_base64: "", curriculo_nome: "" }));
    setFileInfo(null);
    const el = document.getElementById("file-input");
    if (el) el.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!form.consentimento_lgpd) {
      setSubmitError("É necessário aceitar a política de privacidade.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        vacancy_id: id,
        nome: form.nome.trim(),
        email: form.email.trim(),
        telefone: form.telefone.trim(),
        cidade: form.cidade || null,
        escolaridade: form.escolaridade || null,
        experiencia_anos: Number(form.experiencia_anos) || 0,
        pretensao_salarial: Number(form.pretensao_salarial) || 0,
        linkedin: form.linkedin || null,
        carta_apresentacao: form.carta_apresentacao || null,
        curriculo_base64: form.curriculo_base64 || null,
        curriculo_nome: form.curriculo_nome || null,
        consentimento_lgpd: form.consentimento_lgpd,
      };
      await axios.post(`${API_BASE}/public/applications`, payload);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setSubmitError(typeof d === "string" ? d : "Não foi possível enviar. Verifique os dados e tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="max-w-[880px] mx-auto py-20">
          <div className="h-10 w-72 rounded bg-white/5 animate-pulse mb-4" />
          <div className="h-6 w-96 rounded bg-white/5 animate-pulse" />
          <div className="mt-10 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-4 w-full rounded bg-white/5 animate-pulse" />
            ))}
          </div>
        </div>
      </PublicShell>
    );
  }

  if (notFound || !vac) {
    return (
      <PublicShell>
        <div className="max-w-[640px] mx-auto py-24 text-center">
          <AlertCircle className="h-10 w-10 text-[#C5A059] mx-auto mb-4" />
          <h1 className="font-display text-3xl font-semibold">Vaga não encontrada</h1>
          <p className="mt-3 text-white/60 text-[14px]">
            A vaga que você procura não está mais disponível ou foi despublicada.
          </p>
          <Link
            to="/carreiras"
            data-testid="vacancy-back-careers"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-6 py-2.5 text-[13px] font-semibold text-[#1A231F] hover:bg-[#D4B36B] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Ver todas as vagas
          </Link>
        </div>
      </PublicShell>
    );
  }

  if (success) {
    return (
      <PublicShell>
        <div className="max-w-[640px] mx-auto py-24 text-center" data-testid="application-success">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 grid place-items-center mb-5 ring-1 ring-emerald-400/30">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium">
            Candidatura recebida
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-semibold leading-tight">
            Obrigado, {form.nome.split(" ")[0]}!
          </h1>
          <p className="mt-5 text-[14.5px] text-white/70 leading-relaxed">
            Sua candidatura para <b className="text-white">{vac.title}</b> foi registrada
            com sucesso. Nossa equipe de RH fará a análise e entrará em contato pelo e-mail
            informado caso seu perfil avance para a próxima etapa do processo.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/carreiras"
              data-testid="application-explore-more"
              className="inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-6 py-2.5 text-[13px] font-semibold text-[#1A231F] hover:bg-[#D4B36B] transition-colors"
            >
              Explorar outras vagas <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-2.5 text-[12.5px] text-white/80 hover:border-white/30"
            >
              Voltar ao início
            </Link>
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <div className="max-w-[1060px] mx-auto px-6 md:px-0 pt-4 pb-20">
        <Link
          to="/carreiras"
          data-testid="vacancy-back"
          className="inline-flex items-center gap-2 text-[12.5px] text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Todas as vagas
        </Link>

        <div className="mt-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-10">
          {/* Left — Details */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/25 bg-[#C5A059]/[0.08] px-3 py-1 text-[10.5px] uppercase tracking-[0.22em] text-[#E0C07A]">
              <Sparkles className="h-3 w-3" /> Vaga aberta
            </div>

            <h1 className="mt-5 font-display text-3xl md:text-[42px] font-semibold leading-[1.05] tracking-tight" data-testid="vacancy-title">
              {vac.title}
            </h1>
            {vac.cargo && vac.cargo !== vac.title && (
              <div className="mt-2 text-[14px] text-white/60">{vac.cargo}</div>
            )}

            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12.5px] text-white/70">
              {vac.sector_name && <Tag icon={Network}>{vac.sector_name}</Tag>}
              {vac.company_name && <Tag icon={Building2}>{vac.company_name}</Tag>}
              {vac.local_trabalho && <Tag icon={MapPin}>{vac.local_trabalho}</Tag>}
              {vac.tipo_contrato && <Tag icon={Clock}>{vac.tipo_contrato}</Tag>}
              {vac.escolaridade && <Tag icon={GraduationCap}>{vac.escolaridade}</Tag>}
              {vac.quantidade > 1 && <Tag icon={Briefcase}>{vac.quantidade} posições</Tag>}
            </div>

            {vac.descricao && (
              <Section title="Sobre a vaga">
                <p className="whitespace-pre-wrap">{vac.descricao}</p>
              </Section>
            )}
            {vac.requisitos && (
              <Section title="Requisitos">
                <p className="whitespace-pre-wrap">{vac.requisitos}</p>
              </Section>
            )}
            {vac.experiencia && (
              <Section title="Experiência desejada">
                <p className="whitespace-pre-wrap">{vac.experiencia}</p>
              </Section>
            )}
          </div>

          {/* Right — Form */}
          <aside className="lg:sticky lg:top-6 self-start">
            <form
              onSubmit={submit}
              data-testid="application-form"
              className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-white/[0.01] backdrop-blur-sm p-6 md:p-7 shadow-2xl shadow-black/30"
            >
              <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium">
                Candidatar-se
              </div>
              <h2 className="mt-2 font-display text-xl font-semibold tracking-tight">
                Preencha seus dados
              </h2>
              <p className="mt-1 text-[12px] text-white/55">
                Leva menos de 2 minutos. Todos os campos marcados com * são obrigatórios.
              </p>

              <div className="mt-5 space-y-3.5">
                <Field label="Nome completo *">
                  <input
                    required
                    data-testid="form-nome"
                    value={form.nome}
                    onChange={(e) => setField("nome", e.target.value)}
                    className={inputCls}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="E-mail *">
                    <input
                      type="email"
                      required
                      data-testid="form-email"
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Telefone *">
                    <input
                      required
                      data-testid="form-telefone"
                      value={form.telefone}
                      onChange={(e) => setField("telefone", maskPhone(e.target.value))}
                      placeholder="(00) 00000-0000"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  <Field label="Cidade / UF">
                    <input
                      data-testid="form-cidade"
                      value={form.cidade}
                      onChange={(e) => setField("cidade", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                </div>

                <Field label="Escolaridade">
                  <select
                    data-testid="form-escolaridade"
                    value={form.escolaridade}
                    onChange={(e) => setField("escolaridade", e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Selecione...</option>
                    {["Ensino Fundamental", "Ensino Médio", "Técnico", "Superior Incompleto",
                      "Superior Completo", "Pós-graduação", "Mestrado", "Doutorado"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Anos de experiência">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      data-testid="form-experiencia"
                      value={form.experiencia_anos}
                      onChange={(e) => setField("experiencia_anos", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Pretensão (R$)">
                    <input
                      type="number"
                      min="0"
                      data-testid="form-pretensao"
                      value={form.pretensao_salarial}
                      onChange={(e) => setField("pretensao_salarial", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                </div>
                <Field label="LinkedIn">
                  <input
                    data-testid="form-linkedin"
                    value={form.linkedin}
                    onChange={(e) => setField("linkedin", e.target.value)}
                    placeholder="https://linkedin.com/in/..."
                    className={inputCls}
                  />
                </Field>

                <Field label="Currículo (PDF, DOC · até 4 MB)">
                  {!fileInfo ? (
                    <label
                      htmlFor="file-input"
                      className="flex items-center gap-2 justify-center h-[42px] rounded-lg border border-dashed border-white/20 bg-white/[0.03] text-[12.5px] text-white/60 cursor-pointer hover:border-[#C5A059]/50 hover:text-white transition-colors"
                    >
                      <Upload className="h-4 w-4 text-[#C5A059]" />
                      Anexar arquivo
                    </label>
                  ) : (
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-[#C5A059]/25 bg-[#C5A059]/[0.08] px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-[#C5A059] shrink-0" />
                        <span className="truncate text-[12.5px]">{fileInfo.name}</span>
                        <span className="text-[10.5px] text-white/50 shrink-0">
                          {(fileInfo.size / 1024).toFixed(0)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        data-testid="form-remove-file"
                        className="text-[11px] text-white/60 hover:text-white transition-colors"
                      >
                        Remover
                      </button>
                    </div>
                  )}
                  <input
                    id="file-input"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={onFile}
                    data-testid="form-curriculo"
                    className="hidden"
                  />
                  {fileError && (
                    <div className="mt-1.5 text-[11px] text-red-400">{fileError}</div>
                  )}
                </Field>

                <Field label="Carta de apresentação (opcional)">
                  <textarea
                    rows={3}
                    data-testid="form-carta"
                    value={form.carta_apresentacao}
                    onChange={(e) => setField("carta_apresentacao", e.target.value)}
                    placeholder="Conte-nos por que você é ideal para esta vaga..."
                    className={`${inputCls} min-h-[72px] resize-none`}
                  />
                </Field>

                <label className="flex gap-2.5 items-start cursor-pointer pt-1" data-testid="form-lgpd-label">
                  <input
                    type="checkbox"
                    data-testid="form-lgpd"
                    checked={form.consentimento_lgpd}
                    onChange={(e) => setField("consentimento_lgpd", e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-white/20 bg-transparent accent-[#C5A059]"
                  />
                  <span className="text-[11.5px] text-white/60 leading-snug">
                    Autorizo o tratamento dos meus dados pessoais conforme a LGPD,
                    exclusivamente para fins de processo seletivo do Grupo ALCON.
                  </span>
                </label>
              </div>

              {submitError && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-[12px] text-red-300" data-testid="application-error">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>{submitError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="application-submit"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C5A059] px-6 py-3 text-[13.5px] font-semibold tracking-wide text-[#1A231F] shadow-lg shadow-[#C5A059]/20 transition-all hover:bg-[#D4B36B] hover:shadow-[#C5A059]/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Enviando...
                  </>
                ) : (
                  <>
                    Enviar candidatura <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </aside>
        </div>
      </div>
    </PublicShell>
  );
}

const inputCls =
  "w-full h-[40px] rounded-lg bg-[#0B140F]/70 border border-white/10 px-3 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#C5A059]/50 transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-medium mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-10">
      <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium">
        {title}
      </div>
      <div className="mt-3 text-[14px] leading-relaxed text-white/75">{children}</div>
    </div>
  );
}

function Tag({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-[#C5A059]" /> {children}
    </span>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-[#0B140F] text-white">
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#234935]/40 blur-[120px]" />
        <div className="absolute top-1/3 -right-48 h-[480px] w-[480px] rounded-full bg-[#C5A059]/15 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.035] mix-blend-overlay"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)",
            backgroundSize: "3px 3px",
          }}
        />
      </div>
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <Link to="/" className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg bg-gradient-to-br from-[#234935] to-[#1A231F] ring-1 ring-[#C5A059]/30 shadow-lg shadow-black/30">
            <Factory className="h-5 w-5 text-[#C5A059]" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[17px] font-semibold tracking-wide">
              TheoKratos <span className="text-[#C5A059]">RH</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">
              Portal de Carreiras
            </div>
          </div>
        </Link>
        <Link
          to="/carreiras"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2 text-[12.5px] font-medium tracking-wide hover:border-[#C5A059]/50 hover:bg-[#C5A059]/10 transition-colors"
        >
          Todas as vagas <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </header>
      <main className="relative z-10">{children}</main>
      <footer className="relative z-10 border-t border-white/5 px-6 md:px-12 py-8 mt-8">
        <div className="max-w-[1180px] mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] text-white/40">
          <div className="flex items-center gap-2">
            <Factory className="h-3.5 w-3.5 text-[#C5A059]" />
            <span>TheoKratos RH · Portal de Carreiras</span>
          </div>
          <div>© {new Date().getFullYear()} · Grupo ALCON</div>
        </div>
      </footer>
    </div>
  );
}
